
import { useState, useEffect } from "react"
import { getDatabase, ref, onValue, remove, update } from "firebase/database"
import { auth } from "../Firebase/firebase-config"
import { useNavigate } from "react-router-dom"
import { useAccount } from "../context/AccountContext";
import "./UserNotifications.css"

const UserNotifications = ({ onClose }) => {
  const { currentAccount } = useAccount();
  const [notifications, setNotifications] = useState([])
  const [error, setError] = useState("")
  const [activeFilter, setActiveFilter] = useState("all")
  const navigate = useNavigate()

  useEffect(() => {
    const fetchNotifications = () => {
      const userId = currentAccount.uid;
      if (!userId) {
        setError("User not authenticated.")
        return
      }

      const notificationTypes = [
        "Order Dispatch",
        "Query Response",
        "Negotiation Response",
        "Quotation Response",
        "Raised Issues",
        "transportGoods",
        "Steel Orders",
        "machinery orders",
        "Low Balance",
        "Rate Response",
        "Refund Added",
        "Missing_Docs",
        "PO_Alerts",
        "New Code",
        "Partner-Management",
        "StockStatus",
        "uploads",
        "PO Expiry",
        "Order Delay",
        "Document_Uploaded",
        "Firm Request"
           
      ]

      const fetchData = async () => {
        try {
          const allNotifications = []
          const db = getDatabase()
          const currentTimestamp = Date.now()
          const fortyEightHoursInMs = 48 * 60 * 60 * 1000

          // Fetch user-specific notifications
          for (const type of notificationTypes) {
            const notificationsRef = ref(db, `user_notifications/${type}/${userId}`)
            const data = await new Promise((resolve) => {
              onValue(
                notificationsRef,
                (snapshot) => {
                  resolve(snapshot.exists() ? snapshot.val() : {})
                },
                { onlyOnce: true },
              )
            })

            for (const [key, notification] of Object.entries(data)) {
              const notificationTimestamp = notification.timestamp
                ? Date.parse(notification.timestamp) // Convert ISO string to timestamp
                : null

              if (notificationTimestamp && currentTimestamp - notificationTimestamp > fortyEightHoursInMs) {
                await remove(ref(db, `user_notifications/${type}/${userId}/${key}`))
                setNotifications((prev) => prev.filter((n) => n.id !== key)) // Update UI
              } else {
                allNotifications.push({
                  ...notification,
                  id: key,
                  type,
                })
              }
            }
          }

          // Fetch & Delete Expired `NewProduct` Notifications
          const newProductRef = ref(db, `user_notifications/NewProduct`)
          const newProductData = await new Promise((resolve) => {
            onValue(
              newProductRef,
              (snapshot) => {
                resolve(snapshot.exists() ? snapshot.val() : {})
              },
              { onlyOnce: true },
            )
          })

          for (const [supplierId, supplierNotifications] of Object.entries(newProductData)) {
            for (const [key, notification] of Object.entries(supplierNotifications)) {
              const notificationTimestamp = notification.timestamp
                ? Date.parse(notification.timestamp) // Convert ISO 8601 string to timestamp
                : null

              if (notificationTimestamp && currentTimestamp - notificationTimestamp > fortyEightHoursInMs) {
                await remove(ref(db, `user_notifications/NewProduct/${supplierId}/${key}`))
              } else {
                allNotifications.push({
                  ...notification,
                  id: key,
                  type: "NewProduct",
                  supplierId,
                })
              }
            }
          }

          // Fetch & Delete Expired `BrandRates` Notifications
          const brandRatesRef = ref(db, `user_notifications/BrandRates`)
          const brandRatesData = await new Promise((resolve) => {
            onValue(
              brandRatesRef,
              (snapshot) => {
                resolve(snapshot.exists() ? snapshot.val() : {})
              },
              { onlyOnce: true },
            )
          })

          for (const [supplierId, supplierNotifications] of Object.entries(brandRatesData)) {
            for (const [key, notification] of Object.entries(supplierNotifications)) {
              const notificationTimestamp = notification.timestamp
                ? Date.parse(notification.timestamp) // Convert ISO 8601 string to timestamp
                : null

              if (notificationTimestamp && currentTimestamp - notificationTimestamp > fortyEightHoursInMs) {
                await remove(ref(db, `user_notifications/BrandRates/${supplierId}/${key}`))
              } else {
                allNotifications.push({
                  ...notification,
                  id: key,
                  type: "BrandRates",
                  supplierId,
                })
              }
            }
          }

          setNotifications(allNotifications)
        } catch (error) {
          setError("Error fetching notifications.")
          console.error(error)
        }
      }

      fetchData()
    }

    fetchNotifications()
  }, [])

  const handleNotificationClick = (notification) => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) return;

      const db = getDatabase();

      if (notification.type === "NewProduct" || notification.type === "BrandRates") {
        if (!notification.supplierId) return;
        const notificationRef = ref(db, `user_notifications/${notification.type}/${notification.supplierId}/${notification.id}`);
        update(notificationRef, { clicked: true });
      } else {
        const notificationRef = ref(db, `user_notifications/${notification.type}/${userId}/${notification.id}`);
        update(notificationRef, { clicked: true });
      }

      // Navigate
      const routes = {
        "Order Dispatch": "/dashboard/order-status",
        "Order Delay": "/dashboard/order-status",
        "Query Response": "/dashboard/new-query",
        "Negotiation Response": "/dashboard/quote-request",
        "Quotation Response": "/dashboard/quote-request",
        "Raised Issues": "/dashboard/raise-issue",
        "transportGoods": "/dashboard/transport-goods",
        "Low Balance": "/dashboard/order-status",
        "PO Expiry": "/dashboard",
        "Rate Response": "/dashboard/product-order",
        "Refund Added": "",
        BrandRates: "/dashboard/rate-history",
        NewProduct: "/dashboard/newSupplier-products",
        "Missing_Docs": "/dashboard/documents",
        "PO_Alerts": "/dashboard/PO",
        "New Code": "/dashboard/product-order",
        "StockStatus": "/dashboard/stock-status",
        "Uploads": "/dashboard/uploads",
        "Partner-Management": "/dashboard/partner-management",
        "Document_Uploaded":"/dashboard",
        "Firm Request" : "/add-firms"
      };

      navigate(routes[notification.type] || "/");

      // **Notify Navbar to close dropdown**
      if (typeof onClose === "function") onClose();

      // Update local state
      setNotifications((prevNotifications) =>
        prevNotifications.map((n) => n.id === notification.id ? { ...n, clicked: true } : n)
      );
    } catch (error) {
      console.error("Error handling notification click:", error);
    }
  };


  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    switch (type) {
      case "Order Dispatch":
        return <div className="notification-icon dispatch-icon"></div>
      case "Order Delay":
        return <div className="notification-icon delay-icon"></div>
      case "transportGoods":
        return <div className="notification-icon dispatch-icon"></div>
      case "Query Response":
        return <div className="notification-icon query-icon"></div>
      case "Negotiation Response":
        return <div className="notification-icon negotiation-icon"></div>
      case "Quotation Response":
        return <div className="notification-icon quotation-icon"></div>
      case "Raised Issues":
        return <div className="notification-icon issue-icon"></div>
      case "NewProduct":
        return <div className="notification-icon product-icon"></div>
      case "Steel Orders":
        return <div className="notification-icon steel-icon"></div>
      case "machinery orders":
        return <div className="notification-icon machinery-icon"></div>
      case "Low Balance":
        return <div className="notification-icon default-icon"></div>
      case "Rate Response":
        return <div className="notification-icon rates-icon"></div>
      case "BrandRates":
        return <div className="notification-icon rates-icon"></div>
      case "Refund Added":
        return <div className="notification-icon refund-icon" ></div>
      case "Missing_Docs":
        return <div className="notification-icon missing-docs-icon"></div>
      case "PO Expiry":
        return <div className="notification-icon po-icon"></div>
      case "New Code":
        return <div className="notification-icon new-code-icon"></div>
      case "Partner-Management":
      //   return <div className="notification-icon partner-icon"></div>
      case "Document_Uploaded":
        return <div className="notification-icon missing-docs-icon"></div>
        case "Firm Request":
        return <div className="notification-icon firm-icon"></div>
      case "StockStatus":
        return <div className="notification-icon stock-icon"></div>
      case "uploads":
        return <div className="notification-icon uploads-icon"></div>
      default:
        return <div className="notification-icon default-icon"></div>
    }
  }

  // Format timestamp to readable date
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Unknown time"

    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return "Just now"
    if (diffMins < 60) return `${diffMins} min ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`

    return date.toLocaleDateString()
  }

  // Filter notifications based on active filter
  const filteredNotifications =
    activeFilter === "all"
      ? notifications
      : notifications.filter((notification) =>
        activeFilter === "unread" ? !notification.clicked : notification.clicked,
      )

  // Get unique notification types for filter
  const notificationTypes = [...new Set(notifications.map((n) => n.type))]

  if (error) return <div className="notification-error">{error}</div>

  return (
    <div className="modern-notifications">
      <div className="notification-header">
        <h3>Notifications</h3>
        <div className="notification-filters">
          <button
            className={`filter-btn ${activeFilter === "all" ? "active" : ""}`}
            onClick={() => setActiveFilter("all")}
          >
            All
          </button>
          <button
            className={`filter-btn ${activeFilter === "unread" ? "active" : ""}`}
            onClick={() => setActiveFilter("unread")}
          >
            Unread
          </button>
          <button
            className={`filter-btn ${activeFilter === "read" ? "active" : ""}`}
            onClick={() => setActiveFilter("read")}
          >
            Read
          </button>
        </div>
      </div>

      {filteredNotifications.length === 0 ? (
        <div className="notification-empty">
          <div className="empty-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.36 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.63 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16ZM16 17H8V11C8 8.52 9.51 6.5 12 6.5C14.49 6.5 16 8.52 16 11V17Z"
                fill="currentColor"
              />
            </svg>
          </div>
          <p>No notifications to display</p>
        </div>
      ) : (
        <div className="notification-list-container">
          <div className="notification-list">
            {filteredNotifications
              .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
              .map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.clicked ? "unread" : "read"}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-content">
                    {getNotificationIcon(notification.type)}
                    <div className="notification-details">
                      <div className="notification-title">
                        {notification.title || notification.type}
                        {!notification.clicked && notification.gifUrl && (
                          <div className="notification-gif-container">
                            <img
                              src={notification.gifUrl || "/placeholder.svg"}
                              alt="Notification GIF"
                              className="notification-gif"
                            />
                          </div>
                        )}
                      </div>
                      <div className="notification-message">{notification.message}</div>
                      <div className="notification-time">
                        {notification.timestamp ? formatTimestamp(notification.timestamp) : "Invalid date"}
                      </div>
                    </div>
                  </div>


                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default UserNotifications

